import React, { useEffect } from 'react';

import { styled } from '@mui/material/styles';
import {
    Box,
    Button,
    Divider,
    Grid,
    Stack,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import {
    ReactHookFormInput,
    ReactHookFormSelect,
} from 'common/components/form';
import { arraytoDictionary } from 'common/utils/helpers';
import yupResolver from 'common/utils/yupResolver';

import * as actions from '../actions';
import {
    getAllianceViewLevelOptions,
    getIsDisabledFields,
    getRoleLevelOptions,
} from '../helpers';
import * as selectors from '../selectors';
import validate from '../validate';

import PermissionList from './PermissionList';

const FormWrapper = styled(Box)(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === 'dark' ? 'rgb(47, 51, 53)' : '#EEF4FA',
    borderBottomLeftRadius: '18px',
    borderTopLeftRadius: '18px',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '16px',
    minHeight: '100%',
    padding: '16px',
}));

const FormContainer = ({
    fetchRoleLevelDetails,
    fetchRoleLevels,
    financierGroupType,
    handleClickSave,
    initialValues,
    isFetchingRoleGroups,
    isFetchingRoleLevels,
    isNewRecord,
    isSaving,
    roleLevels,
}) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, setValue, watch } = useForm({
        defaultValues: initialValues, // Usar initialValues como valores por defecto
        resolver: yupResolver(validate),
    });

    // Reinicializar el formulario cuando cambien los initialValues
    useEffect(() => reset(initialValues), [initialValues, reset]);

    useEffect(() => {
        fetchRoleLevels(isNewRecord, watch());
    }, [fetchRoleLevels, isNewRecord]);

    const formValues = watch();
    const roleLevelDictionary = arraytoDictionary(roleLevels, 'id');

    const isDisabled = getIsDisabledFields({
        financierGroupType,
        isFetchingRoleLevels,
        isNewRecord,
        roleLevel: roleLevelDictionary[formValues?.financier_group_type] || {},
    });

    const onSubmit = (values) => {
        handleClickSave(values);
    };

    return (
        <Stack component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container>
                <Grid size={5}>
                    <FormWrapper>
                        <Stack>
                            <Typography
                                fontWeight="bold"
                                mb={1}
                                variant="body2"
                            >
                                {t('Role')}
                            </Typography>

                            <ReactHookFormInput
                                control={control}
                                disabled={isDisabled}
                                label={`${t('Role name')} *`}
                                name="name"
                                variant="standard"
                            />

                            <ReactHookFormSelect
                                control={control}
                                disabled={isDisabled}
                                label={`${t('Role level')} *`}
                                name="financier_group_type"
                                onChange={(e) => {
                                    fetchRoleLevelDetails(
                                        e.target.value,
                                        formValues,
                                    );
                                }}
                                options={getRoleLevelOptions(roleLevels)}
                                variant="standard"
                            />

                            <ReactHookFormSelect
                                control={control}
                                disabled={isDisabled}
                                label={`${t('Display level')} *`}
                                name="alliances_visualization_level"
                                options={getAllianceViewLevelOptions()}
                                sx={{ mb: 2 }}
                                variant="standard"
                            />
                        </Stack>
                    </FormWrapper>
                </Grid>

                <Grid size={13}>
                    <PermissionList
                        control={control}
                        formValues={formValues}
                        isDisabled={isDisabled}
                        isFetching={isFetchingRoleGroups}
                        roleLevel={
                            roleLevelDictionary[formValues.financier_group_type]
                        }
                        setValue={setValue}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ mx: (theme) => `${theme.spacing(-2)}!important` }} />

            <Box textAlign="right">
                <Button
                    disabled={isDisabled || isSaving}
                    type="submit"
                    variant="contained"
                >
                    {isNewRecord ? t('Add') : t('Save')}
                </Button>
            </Box>
        </Stack>
    );
};

FormContainer.propTypes = {
    fetchRoleLevelDetails: PropTypes.func,
    fetchRoleLevels: PropTypes.func,
    financierGroupType: PropTypes.object,
    handleClickSave: PropTypes.func,
    initialValues: PropTypes.object,
    isFetchingRoleGroups: PropTypes.bool,
    isFetchingRoleLevels: PropTypes.bool,
    isNewRecord: PropTypes.bool,
    isSaving: PropTypes.bool,
    roleLevels: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
    initialValues: selectors.getInitialValues,
    isFetchingRoleGroups: selectors.getIsFetchingRoleLevelDetails,
    isFetchingRoleLevels: selectors.getIsFetchingRoleLevels,
    isNewRecord: selectors.getIsNewRecord,
    isSaving: selectors.getIsSavingItem,
    roleLevels: selectors.getRoleLevelsData,
});

const mapDispatchToProps = (dispatch) => ({
    fetchRoleLevelDetails: (levelId, formValues) =>
        dispatch(actions.fetchRoleLevelDetails(levelId, formValues)),
    fetchRoleLevels: (isNewRecord, formValues) =>
        dispatch(actions.fetchRoleLevels(isNewRecord, formValues)),
    handleClickSave: (values, company) =>
        dispatch(actions.save(values, company)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FormContainer);
